import Button from '@cobliteam/colib-ui/Button';
import { DialogBody, DialogContent, DialogDescription, DialogFooter, DialogTitle } from '@cobliteam/colib-ui/Dialog';
import Icon from '@cobliteam/colib-ui/Icon';
import Typography from '@cobliteam/colib-ui/Typography';
import type { AlertHookSummaryField } from '@cobliteam/hooks/src/useAlertHooks/alertHookConfig';
import { useTranslation } from '@cobliteam/i18n';
import SummaryRow from './SummaryRow';

interface AlertHookConfirmLayoutProps {
  iconName: string;
  iconBg: string;
  title: string;
  description: string;
  rows: AlertHookSummaryField[];
  editLabel: string;
  onEdit: () => void;
  onActivate: () => void;
  isActivating: boolean;
  onClose: () => void;
}

/** Step 2 body shared by both activation paths: the preview card, the edit link and the footer. */
const AlertHookConfirmLayout = ({ iconName, iconBg, title, description, rows, editLabel, onEdit, onActivate, isActivating, onClose }: AlertHookConfirmLayoutProps) => {
  const { t } = useTranslation(['alertHooks']);
  return (
    <DialogContent size='sm' className='flex flex-col gap-0 p-0' showCloseButton={false}>
      <DialogBody className='px-6 pt-2 pb-4'>
        <div className='mb-4 flex flex-col items-center justify-center pt-8 text-center'>
          <div className={`flex h-14 w-14 items-center justify-center rounded-full ${iconBg}`}>
            <Icon name={iconName as never} size='lg' className='text-link' />
          </div>
          <DialogTitle className='mt-4 text-center'>{title}</DialogTitle>
          <DialogDescription
            render={
              <Typography variant='caption' className='mt-2 gap-1 break-words px-6 text-center text-muted-foreground'>
                {description}
              </Typography>
            }
          />
        </div>

        <div className='overflow-hidden rounded-2xl border border-border'>
          {rows.map((row, index) => (
            <SummaryRow key={row.labelKey} label={t(row.labelKey)} value={t(row.valueKey, row.valueParams)} withDivider={index < rows.length - 1} />
          ))}
          <button
            type='button'
            onClick={onEdit}
            className='flex w-full items-center justify-center gap-2 border-border border-t bg-background px-4 py-3 text-link hover:cursor-pointer hover:bg-background'
          >
            <Icon name='pencil' size='sm' />
            <Typography variant='label' className='gap-1 break-words text-foreground'>
              {editLabel}
            </Typography>
            <Icon name='external-link' size='sm' />
          </button>
        </div>
      </DialogBody>

      <DialogFooter className='flex flex-col gap-2 px-6 pt-4'>
        <Button onClick={onActivate} disabled={isActivating} className='w-full'>
          {t('alertHooks:common.ctaActivate')}
        </Button>
        <Button variant='ghost' onClick={onClose} className='w-full'>
          {t('alertHooks:common.ctaClose')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
};
export default AlertHookConfirmLayout;
